import { useState } from 'react';
import { useStore } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';

const STEPS = ['onboarding.s1', 'onboarding.s2', 'onboarding.s3'] as const;

export function OnboardingOverlay() {
  const t = useT();
  const onboarded = useStore((s) => s.settings.onboarded);
  const setOnboarded = useStore((s) => s.setOnboarded);
  const [step, setStep] = useState(0);

  if (onboarded) return null;

  const isLast = step === STEPS.length - 1;
  const next = (): void => {
    if (isLast) setOnboarded(true);
    else setStep((s) => s + 1);
  };
  const skip = (): void => setOnboarded(true);

  return (
    <div role="dialog" aria-label={t('onboarding.title')} style={overlay}>
      <div style={card}>
        <div style={dotsRow}>
          {STEPS.map((_, i) => (
            <span key={i} style={{ ...dot, background: i === step ? COLORS.accent : COLORS.border }} />
          ))}
        </div>
        <h2 style={title}>{t('onboarding.title')}</h2>
        <p style={body}>{t(STEPS[step]!)}</p>
        <div style={btnRow}>
          <button onClick={skip} style={skipBtn}>
            {t('onboarding.skip')}
          </button>
          <button onClick={next} style={nextBtn} data-testid="onboarding-next">
            {isLast ? t('onboarding.done') : t('onboarding.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 500,
};
const card: React.CSSProperties = {
  background: '#0e0e1c',
  border: '3px solid #ffd6b3',
  padding: SPACING.lg,
  maxWidth: 380,
  width: '90%',
  fontFamily: FONT.family,
  imageRendering: 'pixelated',
  boxShadow: '0 0 0 1px #0a0a14, 4px 4px 0 rgba(255,214,179,0.3)',
};
const dotsRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.xs,
  justifyContent: 'center',
  marginBottom: SPACING.md,
};
const dot: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 0,
};
const title: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  color: COLORS.accent,
  fontSize: FONT.sizeLg,
  textAlign: 'center',
  letterSpacing: 1,
};
const body: React.CSSProperties = {
  color: COLORS.text,
  fontSize: FONT.sizeSm,
  lineHeight: 1.5,
  marginBottom: SPACING.lg,
  textAlign: 'center',
};
const btnRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.sm,
  justifyContent: 'flex-end',
};
const skipBtn: React.CSSProperties = {
  background: 'transparent',
  color: COLORS.textDim,
  border: '1px solid #3a3a5e',
  borderRadius: RADIUS.sm,
  padding: '6px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontFamily: FONT.family,
};
const nextBtn: React.CSSProperties = {
  background: COLORS.accent,
  color: '#000',
  border: 'none',
  borderRadius: RADIUS.sm,
  padding: '6px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
  fontFamily: FONT.family,
};
